import { toast } from 'react-toastify';
import { encrypted } from './utility';

/**
 * Handles a 401 Unauthorized response — shows a session-expired toast,
 * clears all local storage, and redirects the user to the sign-in page.
 * @param {Function} navigate - React Router's navigate function from useNavigate() (optional)
 * @param {string} redirectPath - Path to redirect to (default: "/sign-in")
 * @param {number} delayMs - Redirect delay in ms so the toast is visible (default: 1500)
 */
export const handleUnauthorized = (navigate = null, redirectPath = "/sign-in", delayMs = 1500, message = "Session expired. Please login again.") => {
    const isLocalEnv = window.location.host.includes('localhost');
    
    if (isLocalEnv) {
        toast.error("Unauthorized: Please check your authentication token");
        return;
    }

    toast.error(message);
    localStorage.clear();

    if (navigate && typeof navigate === 'function') {
        setTimeout(() => navigate(redirectPath), delayMs);
    } else {
        setTimeout(() => {
            window.location.href = redirectPath;
        }, delayMs);
    }
};

/**
 * Safely cancels an axios CancelToken source
 * @param {Object} cancelTokenSource - The axios CancelToken source to cancel
 * @param {string} reason - Optional reason message for cancellation (default: "Request cancelled")
 * @param {string} errorContext - Optional context for error logging (e.g., "template API", "file upload")
 * @returns {boolean} - Returns true if cancellation was attempted, false if source was null/undefined
 */
export const cancelCancelToken = (cancelTokenSource, reason = "Request cancelled", errorContext = "request") => {
    if (!cancelTokenSource) return false;

    try {
        cancelTokenSource.cancel(reason);
        return true;
    } catch (err) {
        console.warn(`Error cancelling ${errorContext}:`, err);
        return false;
    }
};

/**
 * Decrypts encrypted error response body
 * @param {string} encryptedBody - The encrypted body from error response
 * @returns {Object|null} - Decrypted error object or null if decryption fails
 */
const decryptErrorResponse = (encryptedBody) => {
    if (!encryptedBody) return null;

    try {
        return JSON.parse(encrypted.decrypt(encryptedBody));
    } catch {
        return null;
    }
};

/**
 * Global function to handle API errors (response is always encrypted)
 * @param {Object} error - The error object from axios/API call
 * @param {Object} toastInstance - The toast object from react-toastify (optional)
 * @param {string} defaultMessage - Default error message if none found (optional)
 * @returns {string} - The error message that was displayed
 */
export const handleApiError = (error, toastInstance = null, defaultMessage = "An error occurred. Please try again.") => {
    const encryptedBody = error?.response?.data?.encryptedBody;
    const errorItem = decryptErrorResponse(encryptedBody);

    const errorMessage = errorItem?.detail
        || errorItem?.message
        || error?.response?.data?.message
        || error?.message
        || defaultMessage;

    if (toastInstance && typeof toastInstance.error === 'function') {
        toastInstance.error(errorMessage);
    }

    return errorMessage;
};

/**
 * Handles authentication API errors and shows toast notification
 * @param {Object} error - The error object from axios/API call
 * @param {Object} toastInstance - The toast object from react-toastify (optional)
 * @param {string} defaultMessage - Default error message if none found (optional)
 * @returns {string} - The error message that was displayed
 */
export const handleAuthError = (error, toastInstance = null, defaultMessage = "Authentication failed. Please try again.") => {
    return handleApiError(error, toastInstance, defaultMessage);
};

/**
 * Extracts assistant content from various AI chat API response formats
 * @param {any} response - The API response object
 * @returns {string} - The extracted assistant content string
 */
export const handleApiResponse = (response) => {
    if (!response) return "";
    if (typeof response === "string") return response;
    if (response?.response && typeof response.response === "string") return response.response;
    if (response?.text) return response.text;
    if (response?.content) return response.content;
    return typeof response === "object" ? JSON.stringify(response) : String(response);
};
